import { useState, useEffect, useCallback } from 'react';
import { GameState } from '../types';
import { storage } from '../utils/helpers';

const initialState: GameState = {
  level: 1,
  totalScore: 0,
  streak: 0,
  hearts: 3,
  achievements: [],
  coins: 0
};

export const useGameState = () => {
  const [gameState, setGameState] = useState<GameState>(initialState);

  // Load saved game state on mount
  useEffect(() => {
    const savedState = storage.getGameState();
    if (savedState) {
      setGameState(savedState);
    }
  }, []);

  // Save game state to localStorage
  const saveGameState = useCallback((newState: Partial<GameState>) => {
    const updatedState = { ...gameState, ...newState };
    setGameState(updatedState);
    storage.setGameState(updatedState);
  }, [gameState]);

  // Reset game state
  const resetGameState = useCallback(() => {
    setGameState(initialState);
    storage.setGameState(initialState);
  }, []);

  // Update specific stats
  const addScore = useCallback((points: number) => {
    saveGameState({
      totalScore: gameState.totalScore + points,
      streak: gameState.streak + 1
    });
  }, [gameState, saveGameState]);

  const addCoins = useCallback((coins: number) => {
    saveGameState({
      coins: gameState.coins + coins
    });
  }, [gameState, saveGameState]);

  const addAchievement = useCallback((achievement: string) => {
    if (!gameState.achievements.includes(achievement)) {
      saveGameState({
        achievements: [...gameState.achievements, achievement]
      });
    }
  }, [gameState, saveGameState]);

  const loseHeart = useCallback(() => {
    const newHearts = Math.max(0, gameState.hearts - 1);
    saveGameState({ hearts: newHearts });
    return newHearts;
  }, [gameState, saveGameState]);

  const gainHeart = useCallback(() => {
    const newHearts = Math.min(5, gameState.hearts + 1);
    saveGameState({ hearts: newHearts });
    return newHearts;
  }, [gameState, saveGameState]);

  const levelUp = useCallback(() => {
    saveGameState({
      level: gameState.level + 1,
      hearts: Math.min(5, gameState.hearts + 1)
    });
  }, [gameState, saveGameState]);

  const resetStreak = useCallback(() => {
    saveGameState({ streak: 0 });
  }, [saveGameState]);

  return {
    gameState,
    saveGameState,
    resetGameState,
    addScore,
    addCoins,
    addAchievement,
    loseHeart,
    gainHeart,
    levelUp,
    resetStreak
  };
};